export default class DateGenerator {
    constructor(resolution = 'hourly') {
        this.selectedResolution = resolution;
    }

    /**
     * Get Adjusted end date based on the selected
     * resolution.
     * @param {Date} end time selected by the user in UTC string.
     * @returns end data object with seconds, minutes or hours
     * reset to zero based on the selected resolution.
     
     * Note: This is because of the sql query at the backend
     * groups the data by hourly (seconds, minutes are zero)
     * or monthly/daily/weekly (hours, minutes
     * and seconds are set to zero)
     */
    getResolutionAdjustedDate(end) {
        const endDate = new Date(end);
        switch (this.selectedResolution) {
            case 'hourly':
                endDate.setMinutes(0);
                endDate.setSeconds(0);
                break;
            case '60-minute':
            case '24-hour':
            case '7-day':
                // For these resolutions, preserve hour/minute, only normalize seconds
                endDate.setSeconds(0);
                break;
            // daily and weekly now handled by default
            default:
                endDate.setHours(0);
                endDate.setMinutes(0);
                endDate.setSeconds(0);
                break;
        }
        return endDate;
    }

    /**
     * Get the next hour
     * @param {Object} date
     * @returns {Object} date object with incremented hour.
     */
    hourlyResolution(date) {
        let nextHour = new Date(date.setHours(date.getHours() + 1));
        nextHour.setMinutes(0);
        nextHour.setSeconds(0);
        return nextHour;
    }

    /**
     * Advances the date by 60 minutes (1 hour).
     *
     * Note: Unlike hourlyResolution, this does not normalize minutes/seconds to zero in the returned date.
     *
     * @param {Object} date - The current date
     * @returns {Object} date object with incremented 60 minutes (minutes/seconds preserved)
     */
    sixtyMinuteResolution(date) {
        let nextHour = new Date(date.setHours(date.getHours() + 1));
        return nextHour;
    }

    /**
     * Get the next day date
     * @param {Object} date
     * @returns {Object} date object with incremented day.
     */
    dailyResolution(date) {
        let nextDate = new Date(date.setDate(date.getDate() + 1));
        nextDate.setHours(0);
        nextDate.setMinutes(0);
        nextDate.setSeconds(0);
        return nextDate;
    }

    /**
     * Advances the date by 24 hours (1 day)
     *
     * Note: Unlike dailyResolution, this does not normalize hours/minutes/seconds to zero in the returned date.
     *
     * @param {Object} date - The current date
     * @returns {Object} date object with incremented 24 hours
     */
    twentyFourHourResolution(date) {
        let nextDate = new Date(date.getTime() + 24 * 60 * 60 * 1000);
        return nextDate;
    }

    /** Get the next week date
     * @param {Object} date
     * @returns {Object} date object with incremented week
     */
    weeklyResolution(date) {
        let nextWeek = new Date(date.setDate(date.getDate() + 7));
        const selectedDate = nextWeek.getDate();
        const dayOfTheWeek = nextWeek.getDay();
        const weekDifference =
            selectedDate - dayOfTheWeek + (dayOfTheWeek === 0 ? -6 : 1);
        const weeklyDate = new Date(nextWeek.setDate(weekDifference));
        weeklyDate.setHours(0);
        weeklyDate.setMinutes(0);
        weeklyDate.setSeconds(0);
        return weeklyDate;
    }

    /**
     * Advances the date by 7 days (rolling period, not aligned to calendar week).
     *
     * Note: This does not normalize hours/minutes/seconds and week days in the returned date; the time and day of the week remain as in the input date.
     *
     * @param {Object} date - The current date
     * @returns {Object} date object with incremented 7 days (time preserved)
     */
    sevenDayResolution(date) {
        let nextWeek = new Date(date.getTime() + 7 * 24 * 60 * 60 * 1000);
        return nextWeek;
    }

    /**
     * Get the next month date.
     * Generate the next month date (1st of the following month)
     * @param {Object} date
     * @returns {Object} date object with incremented month
     */
    monthlyResolution(date) {
        // Clone the input date to avoid modifying the original object
        let nextMonth = new Date(date);

        // consider edge case of jan 31. If the next month is feb, the date will be 31.
        // To avoid this, set the date to 1.
        nextMonth.setDate(1);

        nextMonth.setMonth(nextMonth.getMonth() + 1);
        nextMonth.setHours(0);
        nextMonth.setMinutes(0);
        nextMonth.setSeconds(0);
        return nextMonth;
    }

    getMonthlyDays(date) {
        return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
    }

    /**
     * @returns pointer to function according to the
     * selected resolution.
     */
    getDateGenerator() {
        const resolutionMethodMap = {
            hourly: this.hourlyResolution,
            '60-minute': this.sixtyMinuteResolution,
            daily: this.dailyResolution,
            '24-hour': this.twentyFourHourResolution,
            weekly: this.weeklyResolution,
            '7-day': this.sevenDayResolution,
            monthly: this.monthlyResolution
        };
        return resolutionMethodMap[this.selectedResolution];
    }
}
